import { Link } from "react-router-dom";
import { Star, MapPin, Users, Award, Globe, ArrowRight, ChevronDown } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/2724079/pexels-photo-2724079.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const stats = [
  { number: "500+", label: "Destinos", icon: Globe },
  { number: "5.000+", label: "Viajantes", icon: Users },
  { number: "10+", label: "Anos de Experiência", icon: Award },
  { number: "5.0", label: "Avaliação", icon: Star },
];

const services = [
  { icon: "✈️", title: "Planejamento Personalizado", desc: "Roteiros sob medida para cada tipo de viajante, do pacote econômico ao mais luxuoso." },
  { icon: "🏖️", title: "Pacotes de Férias", desc: "Opções completas para todos os gostos e orçamentos. Voo, hotel e passeios incluídos." },
  { icon: "💎", title: "Viagens de Luxo", desc: "Experiências premium com hotéis 5 estrelas, transfers executivos e concierge exclusivo." },
];

const destinations = [
  { name: "Maldivas", country: "República das Maldivas", category: "Praia", price: "A partir de R$ 8.500", image: "https://images.unsplash.com/photo-1620483829646-8fb1d8162108?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtYWxkaXZlcyUyMHdhdGVyJTIwdmlsbGElMjBhZXJpYWx8ZW58MHx8fHwxNzc0NDQzODc2fDA&ixlib=rb-4.1.0&q=85", big: true },
  { name: "Paris", country: "França", category: "Cidade", price: "A partir de R$ 5.900", image: "https://images.pexels.com/photos/587838/pexels-photo-587838.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Alpes Suíços", country: "Suíça", category: "Aventura", price: "A partir de R$ 7.800", image: "https://images.pexels.com/photos/4159997/pexels-photo-4159997.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Rio de Janeiro", country: "Brasil", category: "Aventura", price: "A partir de R$ 1.200", image: "https://images.pexels.com/photos/6580703/pexels-photo-6580703.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

const testimonials = [
  { name: "Carlos & Mariana", location: "Santos, SP", text: "Atendimento impecável! A equipe da Mahlo cuidou de cada detalhe da nossa lua de mel nas Maldivas. Voltamos cheios de memórias incríveis!", initials: "CM" },
  { name: "Ana Paula", location: "São Paulo, SP", text: "Melhor serviço de viagem que já tive. Personalização incrível e atenção a cada detalhe. Recomendo a todos os meus amigos!", initials: "AP" },
  { name: "Lucas & Thiago", location: "Santos, SP", text: "Como casal LGBTQ+, nos sentimos totalmente acolhidos e respeitados. A viagem para Paris foi simplesmente perfeita!", initials: "LT" },
];

const WA_LINK = "https://wa.me/5513996995877?text=Olá!%20Gostaria%20de%20planejar%20uma%20viagem.";

const Home = () => (
  <div data-testid="home-page">
    {/* Hero */}
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${HERO_IMG})` }} />
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4 sm:px-6">
        <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-4 animate-fade-in">
          Mahlo Viagens &bull; Santos, SP
        </p>
        <h1 className="font-heading text-5xl md:text-7xl font-bold leading-tight mb-6 animate-fade-in-up delay-100">
          Sua próxima aventura
          <span className="block text-[#fff] italic">começa aqui</span>
        </h1>
        <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-300">
          Jornadas excepcionais com serviço extraordinário. Planejamos cada detalhe para que você viva experiências únicas e inesquecíveis.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-500">
          <Link to="/contato" data-testid="hero-cta-primary"
            className="bg-[#89A6A3] text-[#2E4E72] font-bold px-8 py-4 rounded-full text-lg hover:bg-[#6B8C89] hover:text-white hover:scale-105 transition-all duration-300 shadow-lg">
            Planejar Minha Viagem
          </Link>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta"
            className="bg-green-500 text-white font-bold px-8 py-4 rounded-full text-lg hover:bg-green-600 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
            Chamar no WhatsApp
          </a>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 animate-bounce z-10">
        <ChevronDown size={32} />
      </div>
    </section>

    {/* Stats */}
    <section data-testid="stats-section" className="bg-[#2E4E72] py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {stats.map(({ number, label, icon: Icon }) => (
            <div key={label} className="text-center">
              <Icon size={24} className="text-[#89A6A3] mx-auto mb-2" />
              <p className="text-3xl font-bold text-white font-heading">{number}</p>
              <p className="text-slate-300 text-sm mt-1">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* About Preview */}
    <section data-testid="about-preview-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">Sobre Nós</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2E4E72] leading-tight mb-6">
              Viagens planejadas com cuidado e paixão
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              A Mahlo Viagens nasceu do amor por explorar o mundo. Com mais de 10 anos de experiência, somos especialistas em criar experiências de viagem personalizadas que superam expectativas.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              Somos uma agência inclusiva, que acolhe e celebra a diversidade. Acreditamos que todos merecem viver jornadas extraordinárias, independente de quem você é ou com quem ama.
            </p>
            <div className="flex items-center gap-3 mb-8">
              <div className="rainbow-bar w-16" />
              <span className="text-[#2E4E72] font-semibold text-sm">Agência LGBTQ+ Friendly</span>
            </div>
            <Link to="/sobre" data-testid="about-preview-cta"
              className="inline-flex items-center gap-2 text-[#89A6A3] font-bold hover:gap-3 transition-all duration-200">
              Conheça nossa história <ArrowRight size={18} />
            </Link>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://images.pexels.com/photos/3727658/pexels-photo-3727658.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Casal feliz em viagem" className="w-full h-80 md:h-96 object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#2E4E72] text-white p-5 rounded-xl shadow-xl">
              <div className="flex items-center gap-1 mb-1">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="font-bold">5.0 estrelas</p>
              <p className="text-slate-300 text-xs">Avaliação dos clientes</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Services */}
    <section data-testid="services-section" className="py-20 md:py-28 bg-[#F4F7F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">O que oferecemos</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2E4E72]">Nossos Serviços</h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Do planejamento ao retorno, cuidamos de tudo para que sua viagem seja perfeita.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((s) => (
            <div key={s.title} data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="bg-white p-8 rounded-2xl shadow-sm card-hover border border-slate-100">
              <span className="text-4xl mb-4 block">{s.icon}</span>
              <h3 className="font-heading text-xl font-bold text-[#2E4E72] mb-3">{s.title}</h3>
              <p className="text-slate-600 leading-relaxed">{s.desc}</p>
              <Link to="/servicos" className="mt-4 inline-flex items-center gap-1 text-[#89A6A3] font-semibold text-sm hover:gap-2 transition-all">
                Saiba mais <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/servicos" data-testid="all-services-btn"
            className="border-2 border-[#2E4E72] text-[#2E4E72] font-bold px-8 py-3 rounded-full hover:bg-[#2E4E72] hover:text-white transition-all duration-300">
            Ver Todos os Serviços
          </Link>
        </div>
      </div>
    </section>

    {/* Destinations */}
    <section data-testid="destinations-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">Explore o Mundo</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2E4E72]">Destinos Populares</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[220px]">
          {destinations.map((dest, i) => (
            <div key={dest.name} data-testid={`dest-card-${dest.name.toLowerCase()}`}
              className={`relative dest-card rounded-2xl overflow-hidden group cursor-pointer ${dest.big ? "col-span-2 row-span-2" : ""}`}>
              <img src={dest.image} alt={dest.name} className="dest-img w-full h-full object-cover" loading={i < 2 ? "eager" : "lazy"} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2E4E72]/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <span className="text-xs bg-[#89A6A3] text-[#2E4E72] font-bold px-2 py-1 rounded-full">{dest.category}</span>
                <h3 className="font-heading font-bold text-xl mt-1">{dest.name}</h3>
                <p className="text-white/75 text-xs flex items-center gap-1"><MapPin size={10} /> {dest.country}</p>
                {/* <p className="text-[#B0CAC5] text-sm font-semibold mt-1">{dest.price}</p> */}
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/destinos" data-testid="all-destinations-btn"
            className="bg-[#2E4E72] text-white font-bold px-8 py-3 rounded-full hover:bg-[#3A6080] transition-all duration-300 inline-flex items-center gap-2">
            Ver Todos os Destinos <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>

    {/* Testimonials */}
    <section data-testid="testimonials-section" className="py-20 md:py-28 bg-[#F4F7F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">Clientes Felizes</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2E4E72]">O que dizem sobre nós</h2>
          <div className="flex justify-center items-center gap-2 mt-4">
            {[...Array(5)].map((_, i) => <Star key={i} size={20} className="fill-yellow-400 text-yellow-400" />)}
            <span className="text-[#2E4E72] font-bold ml-2">5.0 / 5.0</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((t) => (
            <div key={t.name} data-testid={`testimonial-card-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="bg-white p-8 rounded-2xl shadow-sm card-hover border border-slate-100 flex flex-col">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-slate-600 leading-relaxed flex-1 italic">"{t.text}"</p>
              <div className="flex items-center gap-3 mt-6 pt-4 border-t border-slate-100">
                <div className="w-10 h-10 bg-[#89A6A3] rounded-full flex items-center justify-center text-[#2E4E72] font-bold text-sm">{t.initials}</div>
                <div>
                  <p className="font-bold text-[#2E4E72] text-sm">{t.name}</p>
                  <p className="text-slate-400 text-xs flex items-center gap-1"><MapPin size={10} /> {t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/depoimentos" data-testid="all-testimonials-btn"
            className="inline-flex items-center gap-2 text-[#2E4E72] font-bold hover:text-[#89A6A3] transition-colors">
            Ver todos os depoimentos <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>

    {/* CTA Banner */}
    <section data-testid="cta-banner-section" className="bg-[#2E4E72] py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 opacity-40"
        style={{ backgroundImage: `url(https://images.pexels.com/photos/2724079/pexels-photo-2724079.jpeg?auto=compress&cs=tinysrgb&w=1600)`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      />
      <div className="relative z-10 max-w-4xl mx-auto text-center px-4 sm:px-6">
        <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-4">Vamos começar?</p>
        <h2 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">Pronto para sua próxima aventura?</h2>
        <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">Entre em contato conosco e comece a planejar a viagem dos seus sonhos hoje mesmo.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contato" data-testid="cta-contact-btn"
            className="bg-[#89A6A3] text-[#2E4E72] font-bold px-10 py-4 rounded-full text-lg hover:bg-[#6B8C89] hover:text-white hover:scale-105 transition-all duration-300 shadow-lg">
            Solicitar Orçamento
          </Link>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp-btn"
            className="bg-green-500 text-white font-bold px-10 py-4 rounded-full text-lg hover:bg-green-600 hover:scale-105 transition-all duration-300">
            Chamar no WhatsApp
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default Home;
